import { cn } from '@/lib/utils';

interface TelemetrySparklineProps {
  data: number[];
  color: string;
  height?: number;
  width?: number;
  className?: string;
  showBand?: boolean;
  bandMin?: number;
  bandMax?: number;
}

export function TelemetrySparkline({
  data,
  color,
  height = 24,
  width = 100,
  className = 'opacity-80',
  showBand = false,
  bandMin = 0,
  bandMax = 100,
}: TelemetrySparklineProps) {
  if (!data || data.length < 2) return null;

  const padding = 2;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const points = data
    .map((v, i) => {
      const x = padding + (i / (data.length - 1)) * (width - padding * 2);
      const y = height - padding - ((v - min) / range) * (height - padding * 2);
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <svg width={width} height={height} className={className}>
      {showBand && (
        <rect
          x={padding}
          y={height - padding - ((bandMax - min) / range) * (height - padding * 2)}
          width={width - padding * 2}
          height={((bandMax - bandMin) / range) * (height - padding * 2)}
          fill={color}
          opacity="0.1"
        />
      )}
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface TelemetryMetricRowProps {
  label: string;
  value: string | number;
  unit?: string;
  sparkData?: number[];
  sparkColor?: string;
  sparkWidth?: number;
  sparkHeight?: number;
  sparkShowBand?: boolean;
  sparkBandMin?: number;
  sparkBandMax?: number;
  delta?: number;
  deltaLabel?: string;
  rowClassName?: string;
  labelClassName?: string;
  valueContainerClassName?: string;
  valueClassName?: string;
  unitClassName?: string;
}

export function TelemetryMetricRow({
  label,
  value,
  unit,
  sparkData,
  sparkColor = 'hsl(var(--accent))',
  sparkWidth = 100,
  sparkHeight = 24,
  sparkShowBand = false,
  sparkBandMin = 0,
  sparkBandMax = 100,
  delta,
  deltaLabel,
  rowClassName = 'flex items-center justify-between py-2 border-b border-border/50 last:border-b-0',
  labelClassName = 'text-xs uppercase tracking-wider text-muted-foreground w-16',
  valueContainerClassName = 'flex items-baseline gap-2',
  valueClassName = 'text-lg font-semibold tabular-nums text-foreground',
  unitClassName = 'text-sm text-muted-foreground',
}: TelemetryMetricRowProps) {
  const deltaDisplay =
    delta !== undefined ? (
      <span
        className={cn(
          'text-xs',
          delta > 0
            ? 'text-amber-600 dark:text-amber-400'
            : delta < 0
              ? 'text-green-600 dark:text-green-400'
              : 'text-muted-foreground'
        )}
      >
        {delta > 0 ? '+' : ''}
        {delta.toFixed(1)}%
        {deltaLabel ? ` ${deltaLabel}` : ''}
      </span>
    ) : null;

  return (
    <div className={rowClassName}>
      <div className="flex items-center gap-3">
        <span className={labelClassName}>{label}</span>
        {sparkData && sparkData.length > 2 && (
          <TelemetrySparkline
            data={sparkData}
            color={sparkColor}
            width={sparkWidth}
            height={sparkHeight}
            showBand={sparkShowBand}
            bandMin={sparkBandMin}
            bandMax={sparkBandMax}
          />
        )}
      </div>
      <div className={valueContainerClassName}>
        <span className={valueClassName}>{value}</span>
        {unit && <span className={unitClassName}>{unit}</span>}
        {deltaDisplay}
      </div>
    </div>
  );
}
